import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TeamProject } from '@/types/TeamProject';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export function useTeamProjects() {
  return useQuery({
    queryKey: ['team-projects'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('team_projects' as any)
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter projects where user is a member and get additional data
      const projectsWithMemberData = await Promise.all(
        (data || []).map(async (project: any) => {
          // Check if user is a member of this project
          const { data: membership, error: memberError } = await supabase
            .from('team_project_members' as any)
            .select('role')
            .eq('project_id', project.id)
            .eq('user_id', user.id)
            .single();

          // If user is not a member, skip this project
          if (memberError || !membership) return null;
          
          // Type-safe access to role
          const userRole = (membership as any)?.role || 'member';

          // Get member count
          const { data: memberCount, error: countError } = await supabase
            .from('team_project_members' as any)
            .select('id', { count: 'exact' })
            .eq('project_id', project.id);

          if (countError) throw countError;
          
          return {
            ...project,
            member_count: memberCount?.length || 0,
            user_role: userRole
          } as TeamProject;
        })
      );

      // Filter out null values (projects where user is not a member)
      return projectsWithMemberData.filter(project => project !== null);
    },
  });
}

export function useTeamProject(id: string) {
  return useQuery({
    queryKey: ['team-project', id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('team_projects' as any)
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) return null;

      // Check if user is a member of this project
      const { data: membership, error: memberError } = await supabase
        .from('team_project_members' as any)
        .select('role')
        .eq('project_id', id)
        .eq('user_id', user.id)
        .single();

      // If user is not a member, return null
      if (memberError || !membership) return null;
      
      // Type-safe access to role
      const userRole = (membership as any)?.role || 'member';

      // Get member count
      const { data: memberCount, error: countError } = await supabase
        .from('team_project_members' as any)
        .select('id', { count: 'exact' })
        .eq('project_id', id);

      if (countError) throw countError;

      return {
        ...(data as any),
        member_count: memberCount?.length || 0,
        user_role: userRole
      } as TeamProject;
    },
    enabled: !!id,
  });
}

export function useCreateTeamProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectData: Omit<TeamProject, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'member_count' | 'user_role'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User must be authenticated');

      const { data, error } = await supabase
        .from('team_projects' as any)
        .insert({
          ...projectData,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-projects'] });
      toast({
        title: "Success",
        description: "Team project created successfully.",
      });
    },
    onError: (error) => {
      console.error('Error creating team project:', error);
      toast({
        title: "Error",
        description: "Failed to create team project. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateTeamProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<TeamProject> }) => {
      const { data, error } = await supabase
        .from('team_projects' as any)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-projects'] });
      toast({
        title: "Success",
        description: "Team project updated successfully.",
      });
    },
    onError: (error) => {
      console.error('Error updating team project:', error);
      toast({
        title: "Error",
        description: "Failed to update team project. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useArchiveTeamProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('team_projects' as any)
        .update({ status: 'archived' })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-projects'] });
      toast({
        title: "Success",
        description: "Team project archived successfully.",
      });
    },
    onError: (error) => {
      console.error('Error archiving team project:', error);
      toast({
        title: "Error",
        description: "Failed to archive team project. Please try again.",
        variant: "destructive",
      });
    },
  });
}